import "react-data-grid/lib/styles.css";
import "./Table.scss";

import LoadingIcon from "components/Loading/LoadingIcon";
import { useEffect, useRef, useState } from "react";
import type { DataGridProps, SortColumn } from "react-data-grid";
import DataGrid, { SelectColumn } from "react-data-grid";

import { CustomScrollbars } from "./CustomScrollbars";
import { PaginationControls } from "./PaginationControls";

export type PaginationProps = {
  pageSize: number;
  pageIndex: number;
  rowCount: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
  reportMeta?: Record<string, unknown> | null;
  reportType?: string | null;
  leftContent?: React.ReactNode;
};

export type ServerSortColumn = {
  columnKey: string;
  direction: "ASC" | "DESC";
};

export type ServerSortingProps = {
  sortColumns?: readonly ServerSortColumn[];
  onSortChange?: (sortColumns: readonly ServerSortColumn[]) => void;
};

interface TableProps<T> extends DataGridProps<T> {
  emptyTableMessage?: string;
  rowKeyGetter: (row: T) => string | number;
  rows: T[];
  onSelectedRowsChange?: (selectedRows: Set<string | number>) => void;
  isResizable?: boolean;
  isSortable?: boolean;
  selectedRows?: Set<string | number>;
  rowClass?: (row: T) => string | undefined;
  pagination?: PaginationProps;
  isLoading?: boolean;
  fullHeight?: boolean;
  variant?: "default" | "compact" | "finance";
  drawer?: boolean;
  "data-testid"?: string;
  serverSorting?: ServerSortingProps;
  useServerSorting?: boolean;
  stickyHeader?: boolean;
  customScrollbars?: boolean;
  zebraStripes?: boolean;
  reportMeta?: Record<string, unknown> | null;
  reportType?: string | null;
  sanitizeSelectedRows?: (
    nextSelectedRows: Set<string | number>,
    currentSelectedRows: ReadonlySet<string | number>,
  ) => Set<string | number>;
}

type Key = string | number;

type Comparator<T> = (a: T, b: T) => number;

const Table = <T,>({
  className,
  columns,
  rows,
  rowKeyGetter,
  onSelectedRowsChange,
  emptyTableMessage = "No data available",
  onCellClick,
  isResizable = true,
  isSortable = true,
  selectedRows: externalSelectedRows,
  rowClass,
  pagination,
  isLoading,
  fullHeight = false,
  variant = "default",
  drawer = false,
  "data-testid": testId,
  serverSorting,
  useServerSorting = false,
  stickyHeader = false,
  customScrollbars = false,
  zebraStripes = false,
  reportMeta,
  reportType,
  sanitizeSelectedRows,
}: TableProps<T>) => {
  // client-side sorting state (used when useServerSorting is false)
  const [clientSortColumns, setClientSortColumns] = useState<
    readonly SortColumn[]
  >([]);
  const [sortedRows, setSortedRows] = useState<T[]>(rows);
  const [selectedRows, setSelectedRows] = useState<ReadonlySet<Key>>(
    () => externalSelectedRows || new Set<Key>(),
  );

  // horizontal scroll detection for shadow indicators
  const gridRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: false,
  });

  // convert server sort columns to react-data-grid format for display
  const displaySortColumns: readonly SortColumn[] =
    useServerSorting && serverSorting?.sortColumns
      ? serverSorting.sortColumns.map(({ columnKey, direction }) => ({
          columnKey,
          direction,
        }))
      : clientSortColumns;

  useEffect(() => {
    // eslint-disable-next-line no-console -- intentional dev-time invariant check
    console.assert(
      !(columns?.[0]?.name === "" && !onSelectedRowsChange),
      "onSelectedRowsChange must be provided when using select-row column",
    );
  }, [columns, onSelectedRowsChange]);

  // client-side sorting logic (only used when useServerSorting is false)
  useEffect(() => {
    if (useServerSorting) {
      // for server-side sorting, just use the rows as-is (they're already sorted by the server)
      setSortedRows(rows);
      return;
    }

    if (clientSortColumns.length === 0) {
      setSortedRows(rows);
      return;
    }

    const { columnKey, direction } = clientSortColumns[0];

    const comparator: Comparator<T> = (a, b) => {
      const valueA = a[columnKey as keyof T];
      const valueB = b[columnKey as keyof T];

      if (valueA == null || valueB == null) return 0;
      if (typeof valueA === "string" && typeof valueB === "string") {
        return valueA.localeCompare(valueB);
      }
      if (typeof valueA === "number" && typeof valueB === "number") {
        return valueA - valueB;
      }
      return 0;
    };

    const sorted = [...rows].sort((a, b) => {
      const result = comparator(a, b);
      return direction === "ASC" ? result : -result;
    });

    setSortedRows(sorted);
  }, [rows, clientSortColumns, useServerSorting]);

  useEffect(() => {
    if (externalSelectedRows) {
      setSelectedRows(externalSelectedRows);
    }
  }, [externalSelectedRows]);

  // detect horizontal scroll capability and position
  useEffect(() => {
    const checkScroll = () => {
      const grid = gridRef.current?.querySelector(".rdg");
      if (!grid) return;

      const { scrollLeft, scrollWidth, clientWidth } = grid;
      const canScrollLeft = scrollLeft > 0;
      const canScrollRight = scrollLeft + clientWidth < scrollWidth - 1;

      setScrollState({ canScrollLeft, canScrollRight });
    };

    const grid = gridRef.current?.querySelector(".rdg");
    if (!grid) return;

    // check on mount and when rows change
    checkScroll();

    // add scroll listener
    grid.addEventListener("scroll", checkScroll);
    // check on window resize
    window.addEventListener("resize", checkScroll);

    return () => {
      grid.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [rows, columns]);

  if (rows?.length === 0 && !isLoading) {
    return (
      <div className="no-tasks">
        <div className="no-tasks-header">{emptyTableMessage}</div>
        <div className="no-tasks-icons">
          <div>
            <span className="material-icons">content_paste</span>
          </div>
        </div>
      </div>
    );
  }

  const handleSelectedRowsChange = (newSelectedRows: Set<Key>) => {
    const sanitizedSelectedRows = sanitizeSelectedRows
      ? sanitizeSelectedRows(newSelectedRows, selectedRows)
      : newSelectedRows;

    // when selection is controlled by a parent, avoid optimistic local updates
    // so parent-side sanitization can be applied without select-all flicker.
    if (!externalSelectedRows) {
      setSelectedRows(sanitizedSelectedRows);
    }
    onSelectedRowsChange?.(sanitizedSelectedRows);
  };

  // handle sort column changes
  const handleSortColumnsChange = (newSortColumns: readonly SortColumn[]) => {
    if (useServerSorting && serverSorting?.onSortChange) {
      // convert react-data-grid sort columns to our server sort format
      const serverSortColumns: readonly ServerSortColumn[] = newSortColumns.map(
        ({ columnKey, direction }) => ({
          columnKey,
          direction,
        }),
      );
      serverSorting.onSortChange(serverSortColumns);
    } else {
      // use client-side sorting
      setClientSortColumns(newSortColumns);
    }
  };

  const enhancedRowClass = (row: T) => {
    const baseClass = rowClass?.(row) || "";
    const testClass = testId ? `${testId}-row` : "";
    return [baseClass, testClass].filter(Boolean).join(" ");
  };

  // for finance variant, wrap the DataGrid in a scroll wrapper
  const dataGridContent = (
    <DataGrid
      selectedRows={selectedRows}
      onSelectedRowsChange={handleSelectedRowsChange}
      columns={columns}
      rows={isLoading && rows.length === 0 ? [] : sortedRows}
      rowKeyGetter={rowKeyGetter}
      sortColumns={displaySortColumns}
      onCellClick={onCellClick}
      onSortColumnsChange={handleSortColumnsChange}
      className={`rdg-light table ${className} ${isLoading ? "rdg-loading" : ""}`}
      rowClass={enhancedRowClass}
      defaultColumnOptions={{
        sortable: isSortable,
        resizable: isResizable,
      }}
    />
  );

  return (
    <div
      ref={gridRef}
      className={`table-container ${fullHeight ? "table-container--full-height" : ""} table-container--${variant} ${
        drawer ? "table-container--drawer" : ""
      } ${scrollState.canScrollLeft ? "table-container--scroll-left" : ""} ${
        scrollState.canScrollRight ? "table-container--scroll-right" : ""
      } ${stickyHeader ? "table-container--sticky-header" : ""} ${zebraStripes ? "table-container--zebra-stripes" : ""}`.trim()}
      data-testid={testId}
    >
      {customScrollbars ? (
        <CustomScrollbars>
          {dataGridContent}
          {isLoading && (
            <div className="table-loading-overlay">
              <LoadingIcon size="medium" showText={false} />
            </div>
          )}
        </CustomScrollbars>
      ) : (
        <>
          {dataGridContent}
          {isLoading && (
            <div className="table-loading-overlay">
              <LoadingIcon size="medium" showText={false} />
            </div>
          )}
        </>
      )}
      {pagination && (
        <PaginationControls
          {...pagination}
          isLoading={isLoading}
          reportMeta={reportMeta}
          reportType={reportType}
        />
      )}
    </div>
  );
};

export const SelectColumnWithSelectAll = {
  ...SelectColumn,
  width: 35,
  minWidth: 35,
  maxWidth: 35,
  resizable: false,
};

export const SelectColumnNoSelectAll = {
  ...SelectColumn,
  renderHeaderCell: () => null,
};

export default Table;
